import React from 'react';
import { Loader2, Info, AlertCircle } from '../icons';
import { soundUsedState, type AudioLivenessRow, type SoundUsedState } from './soundLiveness';
import { useIsTruncated } from '../lib/useIsTruncated';

const KNOWN_STATES: ReadonlySet<string> = new Set(['campaign', 'different', 'checking', 'couldnt_check', 'none']);

// Instagram brand gradient — the approved cover-art placeholder shown when
// Instagram exposes no cover for the audio, so a coverless track still reads as
// a filled tile instead of an empty grey box. Same brand mark used for platform
// icon tiles elsewhere (see CreatorReviewPanel / editFieldControls). Exported so
// the campaign-audio field (CampaignDetailsCards) renders the identical tile
// from one source of truth instead of a parallel copy.
export const IG_GRADIENT =
  'linear-gradient(135deg,#FEDA75 0%,#FA7E1E 25%,#D62976 50%,#962FBF 75%,#4F5BD5 100%)';

/**
 * SOUND USED report/live cell (Figma 3966-59108). Five states:
 *   campaign      — cover + track + artist name, default text colour
 *   different     — cover + track (red) + artist name (when known)
 *   checking      — spinner, "Checking…"
 *   couldnt_check — ⓘ, "Unavailable" / "Couldn't check"
 *   none          — em-dash (campaign has no official audio / non-IG)
 *
 * campaign/different render the track's artist as the sub-label instead of a
 * "Campaign sound"/"Different sound" caption — the mismatch is already
 * conveyed by the red track-name text. Original (unlicensed) audio has no
 * artist, so the sub-label is omitted rather than showing a placeholder.
 *
 * Cover art comes from the backend (resolved + stored at check-time), so this is
 * a pure render — no per-row network calls.
 */
export function SoundUsedCell({
  row,
  state: finishedState,
}: {
  row: AudioLivenessRow;
  /** A finished state (the Creator Content report's `sound_used_state`); derived from `row` when absent. */
  state?: SoundUsedState;
}) {
  const reported = finishedState ?? soundUsedState(row);
  const state: SoundUsedState = KNOWN_STATES.has(reported) ? reported : 'couldnt_check';
  // Hooks run unconditionally, before the early returns below (Rules of Hooks).
  const song =
    row.sound_used?.song_name?.trim() || (state === 'different' ? 'Different audio' : 'Campaign audio');
  const [nameRef, nameTruncated] = useIsTruncated<HTMLSpanElement>(song);

  if (state === 'none') {
    return <span className="text-muted-foreground">—</span>;
  }

  if (state === 'checking') {
    // Figma 3966-58041: a plain inline spinner + label, no thumbnail box.
    return (
      <div className="flex items-center gap-2">
        <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />
        <span className="text-[13px] text-muted-foreground">Checking&hellip;</span>
      </div>
    );
  }

  if (state === 'couldnt_check') {
    return (
      <div className="flex min-w-0 items-center gap-2.5">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted">
          <Info className="size-4 text-muted-foreground" />
        </span>
        <div className="min-w-0 leading-tight">
          <div className="truncate text-[13px] font-medium text-foreground">Unavailable</div>
          <div className="truncate text-[12px] text-muted-foreground">Couldn&rsquo;t check</div>
        </div>
      </div>
    );
  }

  // campaign | different — cover thumb + track name + artist sub-label.
  const different = state === 'different';
  const cover = row.sound_used?.cover_url;
  const muted = row.sound_used?.muted_audio;
  const coverStyle = cover ? undefined : { backgroundImage: IG_GRADIENT };
  // Original (unlicensed) audio has no artist — the sub-label is omitted
  // entirely rather than fabricating a placeholder.
  const artist = row.sound_used?.artist_name?.trim();

  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <span
        className="size-9 shrink-0 overflow-hidden rounded-md bg-muted"
        style={coverStyle}
      >
        {cover ? (
          <img src={cover} alt="" className="size-full object-cover" loading="lazy" />
        ) : null}
      </span>
      <div className="min-w-0 leading-tight">
        <div
          className={`flex items-center gap-1 truncate text-[13px] font-medium ${
            different ? 'text-red-600' : 'text-foreground'
          }`}
        >
          <span ref={nameRef} className="min-w-0 truncate" title={nameTruncated ? song : undefined}>
            {song}
          </span>
          {muted && (
            <AlertCircle
              className="size-3.5 shrink-0 text-amber-500"
              aria-label="Instagram muted this audio"
            />
          )}
        </div>
        {artist && <div className="truncate text-[12px] text-muted-foreground">{artist}</div>}
      </div>
    </div>
  );
}
